import React, { useState, useEffect, useRef } from 'react';
import './CoreHR.css';

const CoreHR = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [stats, setStats] = useState({
    employees: 0,
    attendance: 0,
    leaves: 0,
    efficiency: 0
  });

  const sectionRefs = useRef([]);

  // Enhanced HR services with all requested features
  const hrModules = [
    {
      title: "Employee Management",
      description: "Complete employee lifecycle management from onboarding to offboarding. Maintain detailed employee profiles, track career progression, and manage organizational hierarchy.",
      icon: "👨‍💼",
      features: ["Employee Profiles", "Org Chart", "Document Management", "Work History"],
      color: "#667eea"
    },
    {
      title: "Attendance Management",
      description: "Automated attendance tracking with geofencing, biometric integration, and real-time monitoring. Generate comprehensive reports and insights.",
      icon: "⏰",
      features: ["Real-time Tracking", "Biometric Integration", "Geofencing", "Automated Reports"],
      color: "#764ba2"
    },
    {
      title: "Shift Management",
      description: "Intelligent shift scheduling, rotation management, and conflict resolution. Optimize workforce allocation across multiple locations.",
      icon: "🔄",
      features: ["Smart Scheduling", "Shift Rotation", "Conflict Alerts", "Multi-location Support"],
      color: "#f093fb"
    },
    {
      title: "Leave Management",
      description: "Streamlined time-off requests, approval workflows, and accrual tracking. Support for multiple leave types and policies.",
      icon: "🏖️",
      features: ["Leave Requests", "Approval Workflows", "Accrual Tracking", "Policy Management"],
      color: "#4ecdc4"
    },
    {
      title: "Timesheets",
      description: "Digital timesheet management with project tracking, overtime calculation, and integration with payroll systems.",
      icon: "📋",
      features: ["Project Tracking", "Overtime Calculation", "Payroll Integration", "Mobile Access"],
      color: "#ff6b6b"
    },
    {
      title: "HR Help Desk",
      description: "AI-powered HR support system with ticket management, knowledge base, and automated responses for common queries.",
      icon: "🎯",
      features: ["Ticket Management", "AI Support", "Knowledge Base", "SLA Tracking"],
      color: "#ffd93d"
    },
    {
      title: "Document Management",
      description: "Secure document storage, version control, and automated compliance tracking with e-signature capabilities.",
      icon: "📄",
      features: ["Secure Storage", "Version Control", "E-Signatures", "Compliance Tracking"],
      color: "#6bcf7f"
    },
    {
      title: "HR Analytics",
      description: "Advanced analytics and reporting with predictive insights, workforce trends, and performance metrics visualization.",
      icon: "📊",
      features: ["Predictive Analytics", "Workforce Trends", "Performance Metrics", "Custom Reports"],
      color: "#4e54c8"
    }
  ];

  // Professional HR statistics
  const hrStats = [
    { number: "10K+", label: "Employees Managed", icon: "👥" },
    { number: "99.8%", label: "System Uptime", icon: "⚡" },
    { number: "50%", label: "Time Saved", icon: "⏱️" },
    { number: "4.8/5", label: "User Rating", icon: "⭐" }
  ];

  // Enhanced slider images with professional HR themes
  const sliderImages = [
    {
      url: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80",
      title: "Employee Management Dashboard",
      description: "Comprehensive view of your workforce with real-time insights"
    },
    {
      url: "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1469&q=80",
      title: "Attendance & Time Tracking",
      description: "Seamless attendance management across all departments"
    },
    {
      url: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80",
      title: "HR Analytics & Reporting",
      description: "Data-driven decisions with advanced analytics"
    },
    {
      url: "https://images.unsplash.com/photo-1551836026-d5c55ac5d4c5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80",
      title: "Document Management System",
      description: "Secure and organized document handling"
    }
  ];

  // Handle slider navigation
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [sliderImages.length]);

  // Handle page transition and animations
  useEffect(() => {
    setIsVisible(true);
    
    // Animate statistics
    const animateStats = () => {
      setStats({
        employees: 10000,
        attendance: 98.7,
        leaves: 2345,
        efficiency: 45
      });
    };

    setTimeout(animateStats, 1000);

    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('corehr-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Feature rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % hrModules.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [hrModules.length]);

  const scrollToSection = (index) => {
    sectionRefs.current[index]?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  return (
    <div className={`corehr-container ${isVisible ? 'corehr-visible' : ''}`}>
      {/* Enhanced Animated Background */}
      <div className="corehr-background-animation">
        <div className="corehr-floating-shape corehr-shape-1"></div>
        <div className="corehr-floating-shape corehr-shape-2"></div>
        <div className="corehr-floating-shape corehr-shape-3"></div>
        <div className="corehr-floating-shape corehr-shape-4"></div>
        
        {/* Gradient Orbs */}
        <div className="corehr-gradient-orb corehr-orb-1"></div>
        <div className="corehr-gradient-orb corehr-orb-2"></div>
        <div className="corehr-gradient-orb corehr-orb-3"></div>
        
        {/* SVG Background */}
        <svg className="corehr-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="corehr-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(78, 84, 200, 0.1)" />
              <stop offset="50%" stopColor="rgba(143, 148, 251, 0.08)" />
              <stop offset="100%" stopColor="rgba(78, 84, 200, 0.1)" />
            </linearGradient>
            <linearGradient id="corehr-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(102, 126, 234, 0.08)" />
              <stop offset="50%" stopColor="rgba(118, 75, 162, 0.06)" />
              <stop offset="100%" stopColor="rgba(102, 126, 234, 0.08)" />
            </linearGradient>
          </defs>
          <path 
            d="M0,20 Q30,10 60,25 T100,30 L100,100 L0,100 Z" 
            fill="url(#corehr-gradient1)"
            className="corehr-svg-path-1"
          />
          <path 
            d="M0,60 Q40,50 80,65 T100,75 L100,100 L0,100 Z" 
            fill="url(#corehr-gradient2)"
            opacity="0.7"
            className="corehr-svg-path-2"
          />
        </svg>
      </div>

      {/* Hero Section */}
      <section className="corehr-hero-section">
        <div className="corehr-hero-content">
          <div className="corehr-hero-text">
            <div className="corehr-hero-badge">
              <span className="corehr-badge-dot"></span>
              Enterprise HR Platform
            </div>
            <h1 className="corehr-hero-title">
              Transform Your <span className="corehr-gradient-text">Human Resources</span> Management
            </h1>
            <p className="corehr-hero-description">
              Streamline your HR operations with our comprehensive CoreHR platform. 
              From employee onboarding to advanced analytics, we provide everything 
              you need to manage your workforce efficiently and effectively.
            </p>
            
            <div className="corehr-hero-buttons">
              <button className="corehr-btn-primary">
                <span>Start Free Trial</span>
                <div className="corehr-btn-icon">🚀</div>
              </button>
              <button className="corehr-btn-secondary" onClick={() => scrollToSection(1)}>
                View Features
              </button>
              <button className="corehr-btn-tertiary">
                Schedule Demo →
              </button>
            </div>

            {/* Hero Stats */}
            <div className="corehr-hero-stats">
              {hrStats.map((stat, index) => (
                <div key={index} className="corehr-stat">
                  <div className="corehr-stat-icon">{stat.icon}</div>
                  <div className="corehr-stat-content">
                    <div className="corehr-stat-number">{stat.number}</div>
                    <div className="corehr-stat-label">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="corehr-hero-visual">
            {/* Enhanced Image Slider */}
            <div className="corehr-slider-container">
              {sliderImages.map((slide, index) => (
                <div 
                  key={index}
                  className={`corehr-slide ${index === currentSlide ? 'corehr-active' : ''}`}
                  style={{ backgroundImage: `url(${slide.url})` }}
                >
                  <div className="corehr-slide-overlay"></div>
                  <div className="corehr-slide-content">
                    <h3>{slide.title}</h3>
                    <p>{slide.description}</p>
                  </div>
                </div>
              ))}
              
              <div className="corehr-slider-nav">
                {sliderImages.map((_, index) => (
                  <button
                    key={index}
                    className={index === currentSlide ? 'corehr-active' : ''}
                    onClick={() => setCurrentSlide(index)}
                  >
                    <div className="corehr-nav-progress"></div>
                  </button>
                ))}
              </div>

              {/* Floating Feature Cards */}
              <div className="corehr-floating-card corehr-card-1">
                <div className="corehr-card-icon">✅</div>
                <div className="corehr-card-content">
                  <div className="corehr-card-title">Automated</div>
                  <div className="corehr-card-text">Workflows</div>
                </div>
              </div>
              
              <div className="corehr-floating-card corehr-card-2">
                <div className="corehr-card-icon">🔒</div>
                <div className="corehr-card-content">
                  <div className="corehr-card-title">Secure</div>
                  <div className="corehr-card-text">Data</div>
                </div>
              </div>
              
              <div className="corehr-floating-card corehr-card-3">
                <div className="corehr-card-icon">📱</div>
                <div className="corehr-card-content">
                  <div className="corehr-card-title">Mobile</div>
                  <div className="corehr-card-text">Ready</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="corehr-scroll-indicator">
          <div className="corehr-scroll-line"></div>
          <span>Explore CoreHR Features</span>
        </div>
      </section>

      {/* Features Overview Section */}
      <section 
        ref={el => sectionRefs.current[0] = el}
        className="corehr-features-section"
      >
        <div className="corehr-section-content">
          <div className="corehr-section-header">
            <span className="corehr-section-badge">Comprehensive Modules</span>
            <h2>Everything You Need for Modern HR Management</h2>
            <p>Our integrated platform covers all aspects of human resources management with cutting-edge features</p>
          </div>

          <div className="corehr-features-grid">
            {hrModules.map((module, index) => (
              <div 
                key={index}
                className={`corehr-feature-card ${activeFeature === index ? 'corehr-active' : ''} ${hoveredCard === index ? 'corehr-hovered' : ''}`}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ '--accent-color': module.color }}
              >
                <div className="corehr-feature-header">
                  <div className="corehr-feature-icon-wrapper">
                    <div className="corehr-feature-icon">{module.icon}</div>
                    <div 
                      className="corehr-feature-glow"
                      style={{ backgroundColor: module.color }}
                    ></div>
                  </div>
                  <h3>{module.title}</h3>
                </div>
                <p className="corehr-feature-description">{module.description}</p>
                <div className="corehr-feature-list">
                  {module.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="corehr-feature-item">
                      <span className="corehr-feature-check">✓</span>
                      {feature}
                    </div>
                  ))}
                </div>
                <div className="corehr-feature-arrow">
                  <span>Learn More</span>
                  <div className="corehr-arrow-icon">→</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Features Section */}
      <section 
        ref={el => sectionRefs.current[1] = el}
        className="corehr-detailed-section"
      >
        <div className="corehr-section-content">
          {/* Employee Management */}
          <div className="corehr-module-detail">
            <div className="corehr-module-content">
              <span className="corehr-module-badge">Module 1</span>
              <h3>Employee Management</h3>
              <p>
                Complete employee lifecycle management with advanced profile management, 
                organizational chart visualization, and comprehensive work history tracking. 
                Streamline onboarding, promotions, transfers, and offboarding processes.
              </p>
              <div className="corehr-module-features">
                <div className="corehr-module-feature">
                  <div className="corehr-feature-point">
                    <span className="corehr-point-icon">👤</span>
                    <div>
                      <h4>Digital Employee Profiles</h4>
                      <p>Centralized profiles with personal, professional, and document management</p>
                    </div>
                  </div>
                </div>
                <div className="corehr-module-feature">
                  <div className="corehr-feature-point">
                    <span className="corehr-point-icon">🏢</span>
                    <div>
                      <h4>Organizational Chart</h4>
                      <p>Interactive org chart with reporting lines and team structures</p>
                    </div>
                  </div>
                </div>
                <div className="corehr-module-feature">
                  <div className="corehr-feature-point">
                    <span className="corehr-point-icon">📈</span>
                    <div>
                      <h4>Career Progression</h4>
                      <p>Track promotions, role changes, and skill development</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="corehr-module-visual">
              <img 
                src="/images/hr.png" 
                alt="Employee Management" 
              />
              <div className="corehr-visual-overlay"></div>
            </div>
          </div>

          {/* Attendance & Time Management */}
          <div className="corehr-module-detail corehr-reversed">
            <div className="corehr-module-visual">
              <img 
                src="/images/hr2.jpg" 
                alt="Attendance Management" 
              />
              <div className="corehr-visual-overlay"></div>
            </div>
            <div className="corehr-module-content">
              <span className="corehr-module-badge">Module 2</span>
              <h3>Attendance & Time Management</h3>
              <p>
                Advanced attendance tracking with multiple verification methods, 
                real-time monitoring, and comprehensive reporting. Reduce time theft 
                and improve workforce productivity.
              </p>
              <div className="corehr-module-stats">
                <div className="corehr-stat-card">
                  <div className="corehr-stat-value">98.7%</div>
                  <div className="corehr-stat-label">Accuracy Rate</div>
                </div>
                <div className="corehr-stat-card">
                  <div className="corehr-stat-value">40%</div>
                  <div className="corehr-stat-label">Time Saved</div>
                </div>
                <div className="corehr-stat-card">
                  <div className="corehr-stat-value">24/7</div>
                  <div className="corehr-stat-label">Monitoring</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Analytics & Reporting Section */}
      <section 
        ref={el => sectionRefs.current[2] = el}
        className="corehr-analytics-section"
      >
        <div className="corehr-section-content">
          <div className="corehr-section-header">
            <span className="corehr-section-badge">Smart Analytics</span>
            <h2>Data-Driven HR Decisions</h2>
            <p>Transform your HR strategy with actionable insights and predictive analytics</p>
          </div>

          <div className="corehr-analytics-dashboard">
            <div className="corehr-analytics-cards">
              <div className="corehr-analytics-card">
                <div className="corehr-analytics-header">
                  <h4>Workforce Overview</h4>
                  <span className="corehr-analytics-badge">Live</span>
                </div>
                <div className="corehr-analytics-content">
                  <div className="corehr-metric">
                    <span>Total Employees</span>
                    <span>1,247</span>
                  </div>
                  <div className="corehr-metric">
                    <span>Active Today</span>
                    <span>1,089</span>
                  </div>
                  <div className="corehr-metric">
                    <span>On Leave</span>
                    <span>87</span>
                  </div>
                </div>
              </div>

              <div className="corehr-analytics-card">
                <div className="corehr-analytics-header">
                  <h4>Attendance Trends</h4>
                  <span className="corehr-analytics-badge">This Month</span>
                </div>
                <div className="corehr-analytics-chart">
                  <div className="corehr-chart-bar" style={{height: '85%'}}>
                    <div className="corehr-chart-fill"></div>
                  </div>
                  <div className="corehr-chart-bar" style={{height: '92%'}}>
                    <div className="corehr-chart-fill"></div>
                  </div>
                  <div className="corehr-chart-bar" style={{height: '78%'}}>
                    <div className="corehr-chart-fill"></div>
                  </div>
                  <div className="corehr-chart-bar" style={{height: '95%'}}>
                    <div className="corehr-chart-fill"></div>
                  </div>
                </div>
              </div>

              <div className="corehr-analytics-card">
                <div className="corehr-analytics-header">
                  <h4>Leave Distribution</h4>
                  <span className="corehr-analytics-badge">Balance</span>
                </div>
                <div className="corehr-leave-stats">
                  <div className="corehr-leave-item">
                    <span>Sick Leave</span>
                    <span>234</span>
                  </div>
                  <div className="corehr-leave-item">
                    <span>Vacation</span>
                    <span>567</span>
                  </div>
                  <div className="corehr-leave-item">
                    <span>Personal</span>
                    <span>89</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="corehr-cta-section">
        <div className="corehr-cta-background">
          <div className="corehr-cta-orb corehr-cta-orb-1"></div>
          <div className="corehr-cta-orb corehr-cta-orb-2"></div>
        </div>
        <div className="corehr-cta-content">
          <span className="corehr-cta-badge">Get Started</span>
          <h2>Ready to Transform Your HR Operations?</h2>
          <p>Join thousands of companies that trust our CoreHR platform for their human resources management</p>
          <div className="corehr-cta-buttons">
            <button className="corehr-btn-primary corehr-btn-large">
              <span>Start Free Trial</span>
              <div className="corehr-btn-icon">🎯</div>
            </button>
            <button className="corehr-btn-secondary corehr-btn-large">
              Schedule Demo
            </button>
          </div>
          <div className="corehr-cta-features">
            <div className="corehr-cta-feature">
              <span className="corehr-feature-check">✓</span>
              No credit card required
            </div>
            <div className="corehr-cta-feature">
              <span className="corehr-feature-check">✓</span>
              30-day free trial
            </div>
            <div className="corehr-cta-feature">
              <span className="corehr-feature-check">✓</span>
              Setup in 15 minutes
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoreHR;